import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { CoinData } from '../types/interfaces';
import { formatCurrency } from '../utils/currency';

interface PriceComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  cryptoData: CoinData[];
  currency?: string;
  onRequestPayment: () => Promise<boolean>;
}

declare global {
  interface Window {
    Chart: any;
  }
}

export default function PriceComparisonModal({
  isOpen,
  onClose,
  cryptoData,
  currency = 'usd',
  onRequestPayment
}: PriceComparisonModalProps) {
  const [selectedCoins, setSelectedCoins] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<any>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCoins([]);
      setSearchQuery('');
      setShowSearch(false);
      setIsPaid(false);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    }
  }, [isOpen]);

  // Filter coins for search
  const filteredCoins = useMemo(() => {
    if (!searchQuery.trim()) {
      return cryptoData.slice(0, 50);
    }
    const query = searchQuery.toLowerCase();
    return cryptoData
      .filter(coin =>
        coin.name.toLowerCase().includes(query) ||
        coin.symbol.toLowerCase().includes(query) ||
        coin.id.toLowerCase().includes(query)
      )
      .slice(0, 20);
  }, [cryptoData, searchQuery]);

  // Get selected coin data
  const comparisonData = useMemo(() => {
    return selectedCoins
      .map(coinId => cryptoData.find(c => c.id === coinId))
      .filter(Boolean) as CoinData[];
  }, [selectedCoins, cryptoData]);

  // Handle payment
  const handlePayment = async () => {
    if (selectedCoins.length < 2) {
      alert('Please select at least 2 coins to compare');
      return;
    }

    setLoading(true);
    try {
      const success = await onRequestPayment();
      if (success) {
        setIsPaid(true);
      }
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Render comparison chart
  useEffect(() => {
    if (!isPaid || !isOpen || comparisonData.length < 2 || !chartRef.current) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    const colors = [
      'rgb(99, 102, 241)',
      'rgb(139, 92, 246)',
      'rgb(236, 72, 153)',
      'rgb(251, 146, 60)'
    ];

    const datasets = comparisonData.map((coin, index) => ({
      label: `${coin.name} (${coin.symbol.toUpperCase()})`,
      data: [coin.current_price],
      backgroundColor: colors[index % colors.length],
      borderColor: colors[index % colors.length],
      borderWidth: 2
    }));

    chartInstanceRef.current = new window.Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Current Price'],
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: '#cbd5e1',
              padding: 15
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#f1f5f9',
            borderColor: '#6366f1',
            borderWidth: 1,
            callbacks: {
              label: function(context: any) {
                return `${context.dataset.label}: ${formatCurrency(context.parsed.y, currency)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: '#cbd5e1' },
            grid: { display: false }
          },
          y: {
            ticks: {
              color: '#cbd5e1',
              callback: function(value: any) {
                return formatCurrency(value, currency, { compact: true });
              }
            },
            grid: { color: 'rgba(203, 213, 225, 0.1)' }
          }
        }
      }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [isPaid, comparisonData, currency, isOpen]);

  // Handle clicking outside search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showSearch &&
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearch]);

  const addCoin = (coin: CoinData) => {
    if (selectedCoins.length >= 4) {
      alert('Maximum 4 coins can be compared at once');
      return;
    }
    if (selectedCoins.includes(coin.id)) {
      return;
    }
    setSelectedCoins([...selectedCoins, coin.id]);
    setSearchQuery('');
    setShowSearch(false);
  };

  const removeCoin = (coinId: string) => {
    setSelectedCoins(selectedCoins.filter(id => id !== coinId));
  };

  if (!isOpen) return null;

  const maxCoins = 4;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-6xl w-[95%] max-h-[90vh] overflow-y-auto relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center z-10"
        >
          &times;
        </button>

        <div className="text-center mb-6">
          <div className="text-5xl mb-3">⚖️</div>
          <h2 className="text-3xl mb-2 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
            Price Comparison Tool
          </h2>
          <p className="text-text-secondary text-sm">
            Compare up to 4 cryptocurrencies side-by-side
          </p>
        </div>

        {!isPaid ? (
          <div className="space-y-6">
            {/* Coin Selection */}
            <div className="relative">
              <label className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                Select Coins to Compare ({selectedCoins.length}/4):
              </label>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearch(true);
                }}
                onFocus={() => setShowSearch(true)}
                placeholder="🔍 Search coins to add..."
                className="w-full px-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50"
              />
              {showSearch && filteredCoins.length > 0 && (
                <div
                  ref={searchResultsRef}
                  className="absolute z-50 w-full mt-2 bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-2xl border-2 border-primary/20 shadow-bubble-lg max-h-80 overflow-y-auto"
                >
                  {filteredCoins
                    .filter(coin => !selectedCoins.includes(coin.id))
                    .map(coin => (
                      <button
                        key={coin.id}
                        type="button"
                        onClick={() => addCoin(coin)}
                        disabled={selectedCoins.length >= maxCoins}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-bg transition-colors border-b-2 border-border/50 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          {coin.image && (
                            <img
                              src={coin.image}
                              alt={coin.name}
                              className="w-10 h-10 rounded-full"
                              onError={(e) => {
                                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22%3E%3Crect fill=%22%236366f1%22 width=%2240%22 height=%2240%22 rx=%2220%22/%3E%3C/svg%3E';
                              }}
                            />
                          )}
                          <div className="text-left flex-1">
                            <div className="font-bold text-text">{coin.name}</div>
                            <div className="text-sm text-text-secondary">{coin.symbol.toUpperCase()}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-text">{formatCurrency(coin.current_price, currency)}</div>
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Selected Coins */}
            {selectedCoins.length > 0 && (
              <div>
                <label className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                  Selected Coins:
                </label>
                <div className="flex flex-wrap gap-3">
                  {comparisonData.map(coin => (
                    <div
                      key={coin.id}
                      className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-bg to-bg-secondary rounded-2xl border-2 border-primary/20"
                    >
                      {coin.image && (
                        <img
                          src={coin.image}
                          alt={coin.name}
                          className="w-8 h-8 rounded-full"
                          onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22%3E%3Crect fill=%22%236366f1%22 width=%2232%22 height=%2232%22 rx=%2216%22/%3E%3C/svg%3E';
                          }}
                        />
                      )}
                      <div>
                        <div className="font-bold text-text text-sm">{coin.symbol.toUpperCase()}</div>
                        <div className="text-xs text-text-secondary">{formatCurrency(coin.current_price, currency)}</div>
                      </div>
                      <button
                        onClick={() => removeCoin(coin.id)}
                        className="ml-2 text-text-secondary hover:text-danger transition-colors"
                        title="Remove"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Button */}
            <div className="bg-gradient-to-br from-bg to-bg-secondary p-6 rounded-2xl border-2 border-primary/20">
              <p className="text-text-secondary text-sm text-center mb-4">
                <span className="font-bold">Cost:</span> 300 satoshis per comparison session
              </p>
              <button
                onClick={handlePayment}
                disabled={selectedCoins.length < 2 || loading}
                className="w-full px-6 py-4 bg-gradient-to-r from-primary to-primary-light border-none rounded-full text-white text-base font-semibold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-bubble-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : selectedCoins.length < 2 
                  ? 'Select at least 2 coins to compare'
                  : '🔓 Unlock Comparison (300 sats)'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Comparison Chart */}
            <div className="bg-gradient-to-br from-bg to-bg-secondary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble-lg">
              <h3 className="text-xl font-bold text-text mb-4">Price Comparison</h3>
              <div style={{ height: '300px', position: 'relative' }}>
                <canvas ref={chartRef}></canvas>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble-lg overflow-x-auto">
              <h3 className="text-xl font-bold text-text mb-4">Detailed Comparison</h3>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 text-text-secondary font-bold uppercase text-xs tracking-wider">Metric</th>
                    {comparisonData.map(coin => (
                      <th key={coin.id} className="text-center py-3 px-4 text-text font-bold">
                        {coin.name}<br />
                        <span className="text-xs text-text-secondary">{coin.symbol.toUpperCase()}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">Current Price</td>
                    {comparisonData.map(coin => (
                      <td key={coin.id} className="py-3 px-4 text-center text-text font-bold">
                        {formatCurrency(coin.current_price, currency)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">Market Cap</td>
                    {comparisonData.map(coin => (
                      <td key={coin.id} className="py-3 px-4 text-center text-text">
                        {formatCurrency(coin.market_cap, currency, { compact: true })}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">24h Volume</td>
                    {comparisonData.map(coin => (
                      <td key={coin.id} className="py-3 px-4 text-center text-text">
                        {formatCurrency(coin.total_volume, currency, { compact: true })}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">24h Change</td>
                    {comparisonData.map(coin => (
                      <td
                        key={coin.id}
                        className={`py-3 px-4 text-center font-bold ${
                          (coin.price_change_percentage_24h || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {(coin.price_change_percentage_24h || 0) >= 0 ? '+' : ''}
                        {(coin.price_change_percentage_24h || 0).toFixed(2)}%
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">Market Cap Rank</td>
                    {comparisonData.map(coin => (
                      <td key={coin.id} className="py-3 px-4 text-center text-text">
                        #{coin.market_cap_rank}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">24h High</td>
                    {comparisonData.map(coin => (
                      <td key={coin.id} className="py-3 px-4 text-center text-text">
                        {formatCurrency(coin.high_24h, currency)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">24h Low</td>
                    {comparisonData.map(coin => (
                      <td key={coin.id} className="py-3 px-4 text-center text-text">
                        {formatCurrency(coin.low_24h, currency)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-t-2 border-border">
                    <td className="py-3 px-4 text-text-secondary font-semibold">Circulating Supply</td>
                    {comparisonData.map(coin => (
                      <td key={coin.id} className="py-3 px-4 text-center text-text">
                        {coin.circulating_supply ? formatCurrency(coin.circulating_supply, currency, { compact: true }) : 'N/A'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

